import { useMemo } from 'react';

import type { EscritorioResumo } from '../api/client';

interface ResourcePermissionsOptions {
  escritorios: EscritorioResumo[] | undefined;
  isAdmin?: boolean;
  currentUserId?: string;
}

/**
 * Hook que centraliza regras de permissão sobre recursos com origem.
 *
 * Alinhado com authorization.py e check_share_permission (backend):
 * - pessoal: dono tem acesso total
 * - compartilhado: somente leitura
 * - de escritório: escrita/compartilhamento apenas para roles de gestão
 * - sistema (modelos): apenas admin pode editar
 * - admin: bypass em escrita, mas compartilhamento respeita ownership/membership
 */
export function useResourcePermissions({ escritorios, isAdmin = false, currentUserId }: ResourcePermissionsOptions) {
  const escMap = useMemo(() => {
    const map = new Map<string, string>();
    escritorios?.forEach((e) => map.set(e.id, e.meu_role));
    return map;
  }, [escritorios]);

  const hasWriteRole = (escritorioId: string): boolean => {
    const role = escMap.get(escritorioId);
    return role === 'PROPRIETARIO' || role === 'SOCIO';
  };

  /**
   * Pode editar/excluir o recurso?
   * Alinhado com check_resource_access(require_write=True).
   */
  const canWrite = (origem?: string | null, escritorioId?: string | null): boolean => {
    if (isAdmin) return true;
    if (!origem || origem === 'pessoal') return true;
    if (origem === 'compartilhado' || origem === 'sistema') return false;
    if (origem === 'escritorio' && escritorioId) {
      return hasWriteRole(escritorioId);
    }
    return true;
  };

  /**
   * Pode gerenciar compartilhamentos do recurso?
   * Alinhado com check_share_permission: ownership OU membership PROPRIETARIO/SOCIO.
   * Admin NÃO bypassa — precisa ser dono ou membro com role adequado.
   */
  const canShare = (
    origem?: string | null,
    escritorioId?: string | null,
    resourceOwnerId?: string | null,
  ): boolean => {
    if (origem === 'compartilhado' || origem === 'sistema') return false;
    if (origem === 'escritorio' && escritorioId) {
      return hasWriteRole(escritorioId);
    }
    // Pessoal: apenas o dono
    if (!origem || origem === 'pessoal') {
      if (!currentUserId || !resourceOwnerId) return false;
      return resourceOwnerId === currentUserId;
    }
    return false;
  };

  return { canWrite, canShare };
}
